import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Button, Autocomplete, TextField, Checkbox, FormControlLabel, Paper, IconButton, Modal } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbar } from '@mui/x-data-grid';
import editIcon from '../../assets/edit-icon.svg';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import { getNodes, setNodeActive } from '../../services/nodeService';
import { modalContainerSx, modalHeaderSx, modalTitleSx, modalCloseIconSx, modalContentSx, modalWarningRowSx, modalCancelButtonSx, modalDeactivateButtonSx, modalBodyTextSx } from './createNode.styles';
import { pageBoxSx, createButtonWrapperSx, controlsRowSx, headerRowSx, leftTextSx, pageTitleSx, taglineSx, allStatusCombinedSx, nodeDetailsSx, createButtonSx, activeTagSx, inactiveTagSx, nodeNameSx, nodeSubTextSx, nodeCitySx, powerIconSx, checkboxSx, belowMinLabelSx } from './operatingNodes.styles';

type Node = {
    name: string;
    address?: string;
    city: string;
    status: 'Active' | 'Inactive';
    occupancy: string; // e.g. "2 / 3"
    minGuards: number;
    currentGuards: number;
    id?: number;
};

export const activeDotColor = '#60AF97';
export const inactiveDotColor = '#9CA3AF';

const OperatingNodes: React.FC<{ onCreate?: () => void; onEdit?: (node: any) => void; notification?: { show: boolean; message?: string; severity?: 'success' | 'error' } }> = ({ onCreate, onEdit, notification }) => {
    const [powerState, setPowerState] = useState<Record<string, boolean>>(() => {
        return {};
    });

    const togglePower = (name: string) => {
        setPowerState((s) => ({ ...s, [name]: !s[name] }));
    };

    const [rows, setRows] = useState<Node[]>(() => []);
    const [loading, setLoading] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(10);
    const [showPagination, setShowPagination] = useState(true);
    // filter state
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [citySearch, setCitySearch] = useState<string>('');
    const [cityOptions, setCityOptions] = useState<string[]>([]);
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [belowMinOnly, setBelowMinOnly] = useState<boolean>(false);
    const cityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const gridContainerRef = useRef<HTMLDivElement | null>(null);

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    useEffect(() => {
        const container = gridContainerRef.current;
        if (!container) return;
        const scroller = container.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null;
        if (!scroller) return;

        const check = () => {
            const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
            if (rows.length <= paginationModel.pageSize) {
                setShowPagination(true);
            } else {
                setShowPagination(atBottom);
            }
        };

        check();
        scroller.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        return () => {
            scroller.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
        };
    }, [rows.length, paginationModel.pageSize]);

    // confirm deactivate modal
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState<Node | null>(null);

    const openConfirm = (row: Node) => {
        setConfirmTarget(row);
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        setConfirmOpen(false);
        setConfirmTarget(null);
    };

    const confirmToggle = async () => {
        if (!confirmTarget) return;
        // KAN-129: derive action from current status — same flow activates or deactivates.
        const nextActive = confirmTarget.status === 'Inactive';
        try {
            const resp = await setNodeActive((confirmTarget.id ?? 0), nextActive);
            if (resp?.success) {
                // update local state
                setRows((r: Node[]) => r.map((x: Node) => (x.id === confirmTarget.id ? { ...x, status: nextActive ? 'Active' : 'Inactive' } : x)));
                setPowerState((s) => ({ ...s, [confirmTarget.name]: nextActive }));
                window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp.message ?? (nextActive ? 'Node activated' : 'Node deactivated') } }));
            } else {
                window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: resp?.message ?? (nextActive ? 'Failed to activate node' : 'Failed to deactivate node') } }));
            }
        } catch (err: any) {
            console.error('Toggle active failed', err);
            window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? (nextActive ? 'Failed to activate node' : 'Failed to deactivate node') } }));
        } finally {
            closeConfirm();
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Node name',
            flex: 1.6,
            minWidth: 220,
            sortable: false,
            renderCell: (params: GridRenderCellParams<any>) => (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography sx={nodeNameSx}>{params.row.name}</Typography>
                    <Typography sx={nodeSubTextSx}>{params.row.address}</Typography>
                </Box>
            ),
        },
        { field: 'city', headerName: 'City', flex: 0.6, minWidth: 120, sortable: false, renderCell: (params: GridRenderCellParams<any>) => <Typography sx={nodeCitySx}>{params.value}</Typography> },
        {
            field: 'status', headerName: 'Status', flex: 0.6, minWidth: 130, sortable: false, renderCell: (params: GridRenderCellParams<any>) => (
                params.value === 'Active' ? (
                    <Box component="span" sx={activeTagSx}>
                        <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '16777200px', bgcolor: activeDotColor, top: '9.25px', left: '10px', opacity: 1 }} />
                        <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
                    </Box>
                ) : (
                    <Box component="span" sx={inactiveTagSx}>
                        <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '16777200px', bgcolor: inactiveDotColor, top: '9.25px', left: '10px', opacity: 1 }} />
                        <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
                    </Box>
                )
            )
        },
        {
            field: 'occupancy', headerName: 'Guard occupancy', flex: 0.7, minWidth: 140, sortable: false, renderCell: (params: GridRenderCellParams<any>) => {
                const current = Number(params.value.split('/')[0].trim());
                const below = current < params.row.minGuards;
                return (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                        <Typography color={below ? 'error' : 'text.primary'} display="inline">{params.value}</Typography>
                        {below && <Typography component="span" color="error" ml={1}>⚠</Typography>}
                    </Box>
                );
            }
        },
        { field: 'minGuards', headerName: 'Min. guards', flex: 0.4, minWidth: 110, sortable: false, renderCell: (params: GridRenderCellParams<any>) => <Typography>{params.value}</Typography> },
        {
            field: 'actions', headerName: 'Actions', flex: 0.6, minWidth: 140, sortable: false, renderCell: (params: GridRenderCellParams<any>) => {
                const isInactive = params.row?.status === 'Inactive';
                const iconColor = isInactive ? '#9CA3AF' : '#6B7280';
                const showSlash = isInactive || !powerState[params.row.name];
                return (
                    <Box>
                        <IconButton
                            size="small"
                            onClick={() => { if (!isInactive) onEdit && onEdit(params.row); }}
                            disabled={isInactive}
                            aria-disabled={isInactive}
                        >
                            <Box component="img" src={editIcon} alt="Edit" sx={{ width: '14px', height: '14px', opacity: isInactive ? 0.4 : 1, filter: isInactive ? 'grayscale(100%)' : 'none' }} />
                        </IconButton>
                        {/* KAN-129: power toggle stays enabled for inactive rows to allow reactivation. */}
                        <IconButton
                            size="small"
                            title={isInactive ? 'Activate' : 'Deactivate'}
                            onClick={() => { openConfirm(params.row); }}
                        >
                            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PowerSettingsNewIcon sx={{ color: iconColor, ...powerIconSx }} />
                                {showSlash && (
                                    <Box sx={{ position: 'absolute', width: '22px', height: '2px', bgcolor: iconColor, transform: 'rotate(45deg)' }} />
                                )}
                            </Box>
                        </IconButton>
                    </Box>
                );
            }
        },
    ];

    // server-side fetch — reruns when pagination, city search, or status filter changes
    const [rowCount, setRowCount] = useState<number>(0);
    // Tracks whether the initial load produced data; used by the visibility
    // listener to decide whether a re-fetch is needed when the user returns
    // to the tab after a page refresh where Firebase wasn't ready yet.
    const hasDataRef = useRef<boolean>(false);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const apiPage = (paginationModel.page ?? 0) + 1;
                const apiPageSize = paginationModel.pageSize ?? 10;
                // KAN-137: route the city autocomplete through the backend's dedicated
                // `city` filter (strict FK-join, excludes nodes with city = NULL) instead
                // of `search` (OR-fuzzy across name/address/city which leaked blank-city
                // rows whose name/address happened to contain the chosen city name).
                const cityParam = selectedCity || citySearch || undefined;
                const resp = await getNodes(apiPage, apiPageSize, undefined, cityParam);
                if (!mounted) return;
                const mapped = resp.nodes.map((n, idx) => ({
                    id: n.id ?? idx,
                    name: n.name,
                    address: n.address ?? '',
                    latitude: n.latitude ?? null,
                    longitude: n.longitude ?? null,
                    city: n.city ?? '',
                    status: (n.active ? 'Active' : 'Inactive') as 'Active' | 'Inactive',
                    occupancy: `${n.current_guard_occupancy ?? 0} / ${n.minimum_guard_count ?? 0}`,
                    minGuards: n.minimum_guard_count ?? 0,
                    radius: n.geofence_radius_m ?? 0,
                    currentGuards: n.current_guard_occupancy ?? 0,
                }));
                // derive city options from results for the autocomplete dropdown
                const citySet = new Set<string>();
                mapped.forEach((n) => { if (n.city) citySet.add(n.city); });
                setCityOptions(Array.from(citySet));
                // client-side status + below-min filter (these are UI-only, no backend param)
                const filtered = mapped.filter((n) => {
                    if (selectedStatus && n.status !== selectedStatus) return false;
                    if (belowMinOnly && n.currentGuards >= n.minGuards) return false;
                    return true;
                });
                setRows(filtered);
                hasDataRef.current = filtered.length > 0;
                const pw: Record<string, boolean> = {};
                resp.nodes.forEach((n) => { pw[n.name] = Boolean(n.active); });
                setPowerState(pw);
                const total = resp.meta?.total ?? resp.meta?.total_count ?? resp.meta?.count ?? 0;
                setRowCount(Number(total) || mapped.length);
            } catch (err) {
                console.error('Failed to load nodes', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [paginationModel, selectedCity, citySearch, selectedStatus, belowMinOnly]);

    // Safety net: if the very first load produced no rows (e.g. Firebase wasn't
    // ready yet on a hard refresh), re-fetch as soon as the user returns to this
    // tab so they don't have to navigate away and back to see data.
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && !hasDataRef.current) {
                setPaginationModel((m) => ({ ...m })); // trigger re-fetch
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    return (
        <Box sx={pageBoxSx}>
            {/* The create button wrapper is relative so we can position the
      notification absolutely inside it — this keeps the popup visually
      attached to the button and correctly positioned when the page
      layout changes. */}
            <Box mb={2}>
                <Box sx={headerRowSx}>
                    <Box sx={leftTextSx}>
                        <Typography sx={pageTitleSx}>Operating Nodes</Typography>
                        <Typography sx={taglineSx}>Geofenced zones where guards can check in.</Typography>
                    </Box>
                    <Box sx={{ ...createButtonWrapperSx, position: 'relative' }}>
                        <Button variant="contained" sx={createButtonSx} onClick={() => onCreate && onCreate()}>+ Create node</Button>
                        {notification && notification.show && (
                            (() => {
                                const isError = notification.severity === 'error';
                                return (
                            <Box sx={{ position: 'absolute', bottom: 'calc(0%)', right: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: isError ? '#FEF2F2' : '#ECFDF3', border: `1px solid ${isError ? '#FCA5A5' : '#A9DEBF'}`, display: 'flex', alignItems: 'center', px: 2, zIndex: 1200 }}>
                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: isError ? '#B91C1C' : '#008A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                    {isError ? <svg width="13" height="13" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg> : <svg width="13" height="13" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 3L3 5L7 1" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                                </Box>
                                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px' }}>{notification.message}</Typography>
                            </Box>
                                );
                            })()
                        )}
                    </Box>
                </Box>

                <Box sx={controlsRowSx}>
                    <Autocomplete
                        size="small"
                        sx={{ ...allStatusCombinedSx, minWidth: 169 }}
                        options={['Active', 'Inactive']}
                        value={selectedStatus}
                        onChange={(_e, val) => { setSelectedStatus(val); setPaginationModel(m => ({ ...m, page: 0 })); }}
                        renderInput={(params) => <TextField {...params} placeholder="All Statuses" />}
                        clearOnEscape
                    />
                    <Autocomplete
                        size="small"
                        sx={{ ...allStatusCombinedSx, minWidth: 169 }}
                        options={cityOptions}
                        value={selectedCity}
                        inputValue={citySearch}
                        onInputChange={(_e, val, reason) => {
                            if (reason === 'input') {
                                setCitySearch(val);
                                if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
                                cityDebounceRef.current = setTimeout(() => {
                                    setPaginationModel(m => ({ ...m, page: 0 }));
                                }, 350);
                            }
                        }}
                        onChange={(_e, val) => { setSelectedCity(val); setCitySearch(val ?? ''); setPaginationModel(m => ({ ...m, page: 0 })); }}
                        renderInput={(params) => <TextField {...params} placeholder="Search city..." />}
                        filterOptions={(x) => x}
                        clearOnEscape
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={belowMinOnly}
                                onChange={(e) => setBelowMinOnly(e.target.checked)}
                                sx={checkboxSx}
                            />
                        )}
                        label="Below minimum guard count"
                        sx={{ alignItems: 'center', ml: 0, pt: '15px' }}
                        componentsProps={{ typography: { sx: belowMinLabelSx } }}
                    />
                </Box>
            </Box>

            <Paper sx={nodeDetailsSx}>
                <div ref={gridContainerRef} style={{ height: '100%', width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        loading={loading}
                        columns={columns}
                        columnHeaderHeight={30}
                        rowHeight={45}
                        pagination
                            paginationMode="server"
                            paginationModel={paginationModel}
                            onPaginationModelChange={(model: { page: number; pageSize: number }) => {
                                setPaginationModel(model);
                                setPageSize(model.pageSize);
                            }}
                            pageSizeOptions={[5, 10, 25, 50]}
                            rowCount={rowCount}
                        density="comfortable"
                        disableRowSelectionOnClick
                        sx={{
                            '& .MuiDataGrid-root': { position: 'relative' },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#F9FAFB !important',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                minHeight: '40px !important',
                                maxHeight: '40px !important',
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                maxHeight: 'calc(100vh - 260px)',
                                overflow: 'auto',
                                paddingBottom: '56px',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 4,
                                background: '#FFFFFF',
                                borderTop: '1px solid rgba(0,0,0,0.06)'
                            },
                            '& .MuiDataGrid-columnHeader': {
                                backgroundColor: '#F9FAFB !important',
                                minHeight: '40px !important',
                                maxHeight: '40px !important',
                                display: 'flex',
                                alignItems: 'center',
                                borderRight: 'none',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontFamily: 'Inter, Arial, sans-serif',
                                fontWeight: 600,
                                fontSize: '11px',
                                lineHeight: '16.5px',
                                letterSpacing: '0.06px',
                            },
                            '& .MuiDataGrid-columnSeparator': { display: 'none' },
                            '& .MuiDataGrid-cell': { borderRight: 'none' },
                            '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                        }}
                    />
                </div>
            </Paper>

            {/* confirm modal (standardized design) */}
            <Modal open={confirmOpen} onClose={closeConfirm}>
                <Box sx={modalContainerSx}>
                    <Box sx={modalHeaderSx}>
                        <Typography sx={modalTitleSx}>{confirmTarget?.status === 'Inactive' ? 'Activate node?' : 'Deactivate node?'}</Typography>
                        <IconButton size="small" onClick={closeConfirm} sx={modalCloseIconSx}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </IconButton>
                    </Box>
                    <Box sx={modalContentSx}>
                        <Box sx={modalWarningRowSx}>
                            <Box sx={{
                                width: '80px',
                                height: '36px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                backgroundColor: '#FEF2F2',
                                opacity: 1,
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                                p: 0,
                            }}>
                                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px', opacity: 1, display: 'block', objectFit: 'contain' }} />
                            </Box>
                            <Box>
                                <Typography sx={modalBodyTextSx}>{confirmTarget?.status === 'Inactive' ? 'Activating this node will make it available again for guard check-ins.' : 'Deactivated nodes will be unavailable for guard check-ins.'}</Typography>
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" gap={2}>
                            <Button sx={modalCancelButtonSx} onClick={closeConfirm}>Cancel</Button>
                            <Button sx={modalDeactivateButtonSx} onClick={() => { confirmToggle(); }}>{confirmTarget?.status === 'Inactive' ? 'Activate' : 'Deactivate'}</Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default OperatingNodes;
